/**
 * Country-aware address subdivisions (states, provinces, regions), keyed by
 * ISO 3166-1 alpha-2. A country outside the registry falls back to a
 * free-text input. Subdivision `value`s must match Google's
 * `administrative_area_level_1` short name (e.g. `NSW`), or autocomplete-filled
 * addresses won't match the list.
 */

export interface SubdivisionOption {
  value: string;
  label: string;
}

export interface MapView {
  center: { lat: number; lng: number };
  zoom: number;
}

interface CountryConfig {
  /** Noun for the subdivision field, e.g. "State" or "Province". */
  subdivisionLabel: string;
  subdivisions: SubdivisionOption[];
  mapView: MapView;
}

const US_STATES: SubdivisionOption[] = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

const AU_STATES: SubdivisionOption[] = [
  { value: 'ACT', label: 'Australian Capital Territory' },
  { value: 'NSW', label: 'New South Wales' },
  { value: 'NT', label: 'Northern Territory' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'SA', label: 'South Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'WA', label: 'Western Australia' },
];

const CA_PROVINCES: SubdivisionOption[] = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
];

const NZ_REGIONS: SubdivisionOption[] = [
  { value: 'Auckland', label: 'Auckland' },
  { value: 'Bay of Plenty Region', label: 'Bay of Plenty' },
  { value: 'Canterbury Region', label: 'Canterbury' },
  { value: 'Gisborne Region', label: 'Gisborne' },
  { value: "Hawke's Bay Region", label: "Hawke's Bay" },
  { value: 'Manawatū-Whanganui Region', label: 'Manawatū-Whanganui' },
  { value: 'Marlborough Region', label: 'Marlborough' },
  { value: 'Nelson Region', label: 'Nelson' },
  { value: 'Northland Region', label: 'Northland' },
  { value: 'Otago Region', label: 'Otago' },
  { value: 'Southland Region', label: 'Southland' },
  { value: 'Taranaki Region', label: 'Taranaki' },
  { value: 'Tasman Region', label: 'Tasman' },
  { value: 'Waikato Region', label: 'Waikato' },
  { value: 'Wellington Region', label: 'Wellington' },
  { value: 'West Coast Region', label: 'West Coast' },
];

/**
 * Countries with a curated subdivision list. Everything else falls back to a
 * free-text input and the default map view.
 */
const COUNTRY_CONFIG: Readonly<Record<string, CountryConfig | undefined>> = {
  US: {
    subdivisionLabel: 'State',
    subdivisions: US_STATES,
    mapView: { center: { lat: 40, lng: -97 }, zoom: 4 },
  },
  AU: {
    subdivisionLabel: 'State',
    subdivisions: AU_STATES,
    mapView: { center: { lat: -25.3, lng: 133.8 }, zoom: 4 },
  },
  CA: {
    subdivisionLabel: 'Province',
    subdivisions: CA_PROVINCES,
    mapView: { center: { lat: 56.1, lng: -106.3 }, zoom: 4 },
  },
  NZ: {
    subdivisionLabel: 'Region',
    subdivisions: NZ_REGIONS,
    mapView: { center: { lat: -41.3, lng: 174.8 }, zoom: 5 },
  },
};

export const DEFAULT_COUNTRY = 'US';

/** Wording for a country we don't curate — "State" would be wrong for most. */
const DEFAULT_SUBDIVISION_LABEL = 'State/Region';

const DEFAULT_MAP_VIEW: MapView = { center: { lat: 40, lng: -97 }, zoom: 4 };

// ISO 3166-1 alpha-2, kept as a delimited string so the list stays readable.
// Labels are resolved at runtime via Intl.DisplayNames rather than
// hand-maintaining 249 country names.
const ISO_COUNTRY_CODES: string[] = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ ' +
  'BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM ' +
  'DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS ' +
  'GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN ' +
  'KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ ' +
  'MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM ' +
  'PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV ' +
  'SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI ' +
  'VN VU WF WS YE YT ZA ZM ZW'
).split(' ');

const ISO_COUNTRY_CODE_SET = new Set(ISO_COUNTRY_CODES);

// Everything Intl-backed below is built lazily and memoized. React Native's
// Hermes engine implements neither Intl.DisplayNames nor Intl.Locale natively,
// so mobile has to polyfill them, and polyfilled Intl is markedly slower than
// native. Building 249 country names eagerly would put that cost on every cold
// start whether or not an address form is ever opened.

let displayNames: Intl.DisplayNames | null | undefined;

const countryDisplayNames = (): Intl.DisplayNames | null => {
  if (displayNames === undefined) {
    try {
      displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    } catch {
      // Unavailable and unpolyfilled; fall back to raw codes as labels.
      displayNames = null;
    }
  }

  return displayNames;
};

const countryLabel = (code: string): string => countryDisplayNames()?.of(code) ?? code;

let cachedCountryOptions: SubdivisionOption[] | undefined;

/**
 * Options for the address Country dropdown, alphabetical by country name.
 * Resolved on first use — call it from render, don't hoist it to a module const.
 */
export const countryOptions = (): SubdivisionOption[] => {
  cachedCountryOptions ??= ISO_COUNTRY_CODES.map((value) => ({
    value,
    label: countryLabel(value),
  })).sort((a, b) => a.label.localeCompare(b.label));

  return cachedCountryOptions;
};

let cachedDisplayNameToCode: Map<string, string> | undefined;

const displayNameToCode = (): Map<string, string> => {
  if (cachedDisplayNameToCode === undefined) {
    const byName = new Map<string, string>();

    ISO_COUNTRY_CODES.forEach((code) => {
      const label = countryLabel(code);
      if (label !== code) byName.set(label.toUpperCase(), code);
    });

    cachedDisplayNameToCode = byName;
  }

  return cachedDisplayNameToCode;
};

/**
 * Best-effort ISO 3166-1 alpha-2 code for a free-text country value.
 *
 * Accepts an ISO code or an English country name ("Australia", "United
 * States"); informal abbreviations like "USA" and "UK" are not recognised,
 * because migration 20260818120100 normalized them out of the column. Returns
 * null when the value isn't recognisable (including empty/null), so callers
 * fall back to the tenant's own country.
 */
export const normalizeCountryCode = (raw: string | null | undefined): string | null => {
  if (raw === null || raw === undefined) return null;

  const cleaned = raw.replace(/[.]/g, '').replace(/\s+/g, ' ').trim().toUpperCase();

  if (cleaned === '') return null;
  if (ISO_COUNTRY_CODE_SET.has(cleaned)) return cleaned;

  return displayNameToCode().get(cleaned) ?? null;
};

/** ISO country code for a BCP 47 locale, e.g. `en-AU` -> `AU`. */
export const regionFromLocale = (locale: string | null | undefined): string | null => {
  if (locale === null || locale === undefined || locale === '') return null;

  try {
    const region = new Intl.Locale(locale).region;
    return region !== undefined && region !== '' ? region.toUpperCase() : null;
  } catch {
    // Malformed locale; treat as unknown.
    return null;
  }
};

/**
 * Which country's subdivisions an address should offer.
 *
 * Most existing addresses have no country at all (the column is null for the
 * majority of rows, including most of our Australian and Canadian tenants'),
 * so the tenant's locale is the fallback rather than an afterthought.
 */
export const resolveAddressCountry = (
  addressCountry: string | null | undefined,
  tenantLocale: string | null | undefined,
): string =>
  normalizeCountryCode(addressCountry) ?? regionFromLocale(tenantLocale) ?? DEFAULT_COUNTRY;

/**
 * Curated subdivisions for a country, or null when we don't have a list and the
 * caller should render a free-text input instead.
 */
export const subdivisionsFor = (country: string | null | undefined): SubdivisionOption[] | null =>
  COUNTRY_CONFIG[country ?? '']?.subdivisions ?? null;

/** Field label for a country's subdivision, e.g. "State" or "Province". */
export const subdivisionLabelFor = (country: string | null | undefined): string =>
  COUNTRY_CONFIG[country ?? '']?.subdivisionLabel ?? DEFAULT_SUBDIVISION_LABEL;

/** Default map center/zoom for a country, used when nothing is geocoded yet. */
export const countryMapView = (country: string | null | undefined): MapView | null =>
  COUNTRY_CONFIG[country ?? '']?.mapView ?? null;

export const mapViewFor = (country: string | null | undefined): MapView =>
  countryMapView(country) ?? DEFAULT_MAP_VIEW;

/**
 * Makes `current` selectable even when it isn't one of `options`.
 *
 * Dropdowns render a value outside their options as blank, which would turn
 * legacy data ('ONT', 'USA') into an empty required field the user can't
 * recover. Prepending the stored value keeps it visible and editable without
 * rewriting it.
 */
export const withCurrentOption = (
  options: SubdivisionOption[],
  current: string | null | undefined,
): SubdivisionOption[] => {
  if (current === null || current === undefined || current === '') return options;
  if (options.some((option) => option.value === current)) return options;

  return [{ value: current, label: current }, ...options];
};
